import React, { useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
} from '@mui/material';

const GoalStaticsTotalDetailTable = ({ 
  loading, 
  data, 
  budgets, 
  tableRef, 
  deptList
}) => {
  const rowDefinitions = [
    { label: '매출 목표', key: 'statAmt', isBoldRow: true },
    { label: '매출이익 목표', key: 'statProfit', isBoldRow: true },
    { label: '일반관리 예산', key: 'CALC_MGMT', isCalc: true, isHighlight: true, isBoldRow: true },
    { label: '직접비 예산', key: 'directAmt', isHighlight: true },
    { label: '공통비 예산', key: 'commonBudgetAmt, strategyBudgetAmt', isHighlight: true },
    { label: '공통비 배부율 (공통)', key: 'commonRate', isRate: true, isHighlight: true },
    { label: '공통비 배부율 (전략)', key: 'strategyRate', isRate: true, isHighlight: true },
    { label: '영업이익 목표', key: 'statOperatingProfit', isBoldRow: true },
  ];

  const { dataMap, totalMap } = useMemo(() => {
    const safeData = Array.isArray(data) ? data : (data?.list || []);
    const safeBudgets = Array.isArray(budgets) ? budgets : (budgets?.list || []);
    const dMap = {}; 
    const tMap = {};
    rowDefinitions.forEach(row => { tMap[row.key] = 0; });

    const validUuids = new Set(deptList?.map(d => d.dptUuid) || []);
    const lastUuid = deptList && deptList.length > 0 ? deptList[deptList.length - 1].dptUuid : null;

    [...safeData, ...safeBudgets].forEach(item => {
      const currentUuid = item.dptUuid;
      const targetUuid = validUuids.has(currentUuid) ? currentUuid : lastUuid;
      rowDefinitions.forEach(row => {
        if (row.isCalc) return;
        const keys = row.key.split(',');
        const val = keys.reduce((sum, k) => sum + (Number(item[k.trim()]) || 0), 0);
        tMap[row.key] += val;
        if (targetUuid) {
          if (!dMap[targetUuid]) dMap[targetUuid] = {};
          dMap[targetUuid][row.key] = (Number(dMap[targetUuid][row.key]) || 0) + val;
        }
      });
    });
    return { dataMap: dMap, totalMap: tMap };
  }, [data, budgets, deptList]); 

  const columns = useMemo(() => {
    const teams = deptList?.map(dept => ({ id: dept.dptUuid, name: dept.dptNm })) || [];
    const total = [{ id: 'total', name: 'TOTAL' }];
    return [...teams, ...total]; 
  }, [deptList]);

  return (
    <Box sx={{ my: -3, width: '100%' }}>
          <Typography 
      sx={{ 
        fontSize: '0.75rem', 
        color: '#888', 
        textAlign: 'right', // 텍스트 우측 정렬
        mb: 0.5,            // 테이블과의 간격
        pr: 1               // 오른쪽 끝에서 살짝 띄움
      }}
    >
      (단위:원)
    </Typography>
      <Paper elevation={1} sx={{ overflow: 'auto' }}>
        <TableContainer>
          <Table 
            size="small" 
            ref={tableRef}
            sx={{ 
              tableLayout: 'fixed',
              borderCollapse: 'collapse',
              '& .MuiTableCell-root': {
                border: '1px solid #e0e0e0',
                padding: '12px 10px',
                fontSize: '0.875rem',
                height: '40px',
                minWidth: '130px',
                lineHeight: 1.4,
              }
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ backgroundColor: '#e3f2fd', fontWeight: 'bold', width: '200px', position: 'sticky', left: 0, zIndex: 2 }}>
                  구분
                </TableCell>
                {columns.map((col) => (
                  <TableCell key={col.id} align="center" sx={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}>
                    {col.name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rowDefinitions.map((row) => {
                // 들여쓰기 대상 행들을 명확히 구분 (일반관리 예산 제외)
                const isSubItem = [
                  'directAmt', 
                  'commonBudgetAmt, strategyBudgetAmt', 
                  'commonRate', 
                  'strategyRate'
                ].includes(row.key);

                return (
                  <TableRow key={row.key}>
                    <TableCell 
                      align="left" 
                      sx={{ 
                        backgroundColor: row.isHighlight ? '#fff3e0 !important' : '#fafafa',
                        fontWeight: 600,
                        position: 'sticky', 
                        left: 0, 
                        zIndex: 1,
                        // 하위 항목은 48px, 상위 항목은 16px로 좌측 여백 차별화
                        paddingLeft: isSubItem ? '50px !important' : '25px !important',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {row.label}
                    </TableCell>

                    {columns.map((col) => {
                      const isTotal = col.id === 'total';
                      let val = 0;

                      if (row.key === 'CALC_MGMT') {
                        const directKey = 'directAmt';
                        const commonKey = 'commonBudgetAmt, strategyBudgetAmt';
                        const dVal = isTotal ? (totalMap[directKey] || 0) : (dataMap[col.id]?.[directKey] || 0);
                        const cVal = isTotal ? (totalMap[commonKey] || 0) : (dataMap[col.id]?.[commonKey] || 0);
                        val = dVal + cVal;
                      } else {
                        val = isTotal ? (totalMap[row.key] || 0) : (dataMap[col.id]?.[row.key] || 0);
                      }

                      const displayValue = row.isRate 
                        ? `${Math.round(val)}%` 
                        : Math.round(val).toLocaleString();

                      return (
                        <TableCell 
                          key={`${row.key}-${col.id}`} 
                          align="right"
                          sx={{ 
                            backgroundColor: isTotal ? '#edf2f7' : 'inherit', 
                            fontWeight: (isTotal || row.isBoldRow) ? 'bold' : 'normal',
                            color: isTotal ? '#1976d2' : 'inherit'
                          }}
                        >
                          {displayValue}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default GoalStaticsTotalDetailTable;
